<template>
  <div class="ui-street-suggest">
    <ui-input
      ref="query"
      v-model="query"
      :label="label"
      autocomplete="off"
      :placeholder="placeholder"
      :required="required"
      validate="street"
      clearable
      @update:model-value="getSuggestions"
      @focus="onFocus"
      @blur="onBlur"
    />

    <div class="ui-street-suggest__suggestions">
      <ui-dropdown
        :visible="showSuggestions"
      >
        <div
          v-for="(suggestion, ind) of formattedSuggestions"
          :key="ind"
          class="ui-street-suggest__suggestion"
          @mousedown="select(suggestion)"
        >
          {{ suggestion.text }}

          <span class="ui-street-suggest__suggestion__details">
            {{ suggestion.moniker }}
          </span>
        </div>
      </ui-dropdown>
    </div>
  </div>
</template>

<script>
import { debounce } from 'throttle-debounce';

import IconCheck from '@icons/check.svg';

import { cartDeliveryGtmEvents } from '@/gtm/cart';

const addressToString = ({ kind, name } = {}) => {
  switch (kind) {
    case 'Проспект': return `${name} пр-т`;
    case 'Проезд': return `${name} пр.`;
    case 'Улица': return `ул. ${name}`;
    case 'Переулок': return `${name} пер.`;
    case 'Набережная': return `наб. ${name}`;
    case 'Тупик': return `${name} туп.`;
    case undefined: return name;
    default: return `${kind}, ${name}`;
  }
};

export default {
  name: 'ui-street-suggest',

  icons: {
    IconCheck,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },

    required: {
      type: Boolean,
      default: false,
    },

    cityId: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: 'Название улицы',
    },

    label: {
      type: String,
      default: 'Улица *',
    },
  },

  data: () => ({
    isMounted: false,
    query: '',
    suggestions: [],
    isSelectedFromSuggest: false,
    isFocused: false,
  }),

  computed: {
    formattedSuggestions() {
      return this.suggestions.map((s) => ({
        ...s,
        text: addressToString(s),
      }));
    },
    showSuggestions() {
      return this.isMounted && this.isFocused && !!this.suggestions.length;
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        this.query = val?.name;
      },
    },
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    select(street) {
      if (street) {
        this.isSelectedFromSuggest = true;
        this.$emit('update:modelValue', street);
      }
    },

    input() {
      if (!this.isSelectedFromSuggest && this.query !== this.modelValue?.name) {
        this.$emit('update:modelValue', { name: this.query });
      }
    },

    getSuggestions: debounce(300, false, function (query = this.query) {
      this.input();

      if (query.length < 2 || !this.cityId) {
        if (this.suggestions.length) this.suggestions = [];
        return;
      }
      this.$store.$api.findStreet({ query, cityId: this.cityId }).then((streets) => {
        this.suggestions = streets;
      }).catch(() => {
        this.$logger.warn(`По запросу "${this.query}" ничего не найдено`);
      });
    }),

    // для внешнего вызова
    checkValidOutside() {
      if (this.$refs.query) return this.$refs.query.checkValidOutside();
    },

    onFocus() {
      this.isFocused = true;
      this.getSuggestions();
    },

    onBlur() {
      if (this.query.length > 0) {
        cartDeliveryGtmEvents.courierTabAddressInteraction();
      }
      this.query = this.query.trim();
      this.input();
      this.isFocused = false;
      this.isSelectedFromSuggest = false;
      this.suggestions = [];
    },
  },
};
</script>

<style lang="scss">
.ui-street-suggest {
  position: relative;

  &__suggestions {
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;

    .ui-dropdown {
      margin-top: 8px;
      width: 100%;
    }

    .ui-dropdown-scrollview {
      max-height: 216px;
    }
  }

  &__suggestion {
    margin-top: 0;
    padding: 8px 12px;
    line-height: 24px;
    cursor: pointer;

    &__details {
      display: block;
      font-size: 12px;
      line-height: 20px;
      color: $gray;
    }

    &:hover {
      background-color: $gray_bgr;
    }
  }
}
</style>
